import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Music, Heart, MessageSquare, Loader2, CheckCircle2, Radio, BarChart3, Trophy, Gift, Mic } from "lucide-react";
import PublicRedeem from "./PublicRedeem";
import PublicVoiceWall from "./PublicVoiceWall";

const B = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  ["request", "Song Request", Music],
  ["dedication", "Dedication", Heart],
  ["message", "Message", MessageSquare],
  ["vote", "Vote", BarChart3],
  ["win", "Win", Trophy],
];

const DTYPES = [["shoutout", "Shout-Out"], ["love", "Love"], ["birthday", "Birthday"],
  ["anniversary", "Anniversary"], ["congratulations", "Congratulations"],
  ["memorial", "Memorial"], ["song_dedication", "Song Dedication"]];

const inputCls = "w-full rounded-md bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:border-amber-400/70";

export default function PublicEngage() {
  const { stationId } = useParams();
  const [params] = useSearchParams();
  const [station, setStation] = useState(null);
  const [tab, setTab] = useState(TABS.some(([k]) => k === params.get("tab")) || ["redeem", "voices"].includes(params.get("tab")) ? params.get("tab") : "request");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [err, setErr] = useState("");
  const [f, setF] = useState({});
  const [polls, setPolls] = useState([]);
  const [voted, setVoted] = useState({});
  const [contests, setContests] = useState([]);
  const [entered, setEntered] = useState({});
  const [pts, setPts] = useState(0);
  const [claimed, setClaimed] = useState({});
  const [wall, setWall] = useState(null);
  const accent = station?.branding?.accent_color || "#fbbf24";

  useEffect(() => {
    axios.get(`${B}/api/engagement/public/${stationId}/info`)
      .then((r) => setStation(r.data)).catch(() => setStation({ missing: true }));
    axios.get(`${B}/api/engagement/public/${stationId}/voice-wall`)
      .then((r) => setWall(r.data)).catch(() => {});
  }, [stationId]);

  useEffect(() => {
    if (tab === "vote") axios.get(`${B}/api/engagement/public/${stationId}/polls`).then((r) => setPolls(r.data.items || []));
    if (tab === "win") axios.get(`${B}/api/engagement/public/${stationId}/contests`).then((r) => setContests(r.data.items || []));
  }, [tab, stationId]);

  const vote = async (pollId, optionKey) => {
    try {
      const r = await axios.post(`${B}/api/engagement/public/${stationId}/polls/${pollId}/vote`, { option_key: optionKey });
      setVoted((v) => ({ ...v, [pollId]: true }));
      setPolls((ps) => ps.map((p) => (p.poll_id === pollId ? { ...p, ...r.data.results } : p)));
    } catch (e2) {
      if (e2?.response?.status === 409) setVoted((v) => ({ ...v, [pollId]: true }));
      else setErr(e2?.response?.data?.detail || "Vote failed");
    }
  };

  const claim = async (pollId) => {
    try {
      const r = await axios.post(`${B}/api/engagement/public/${stationId}/polls/${pollId}/claim`,
        { email: f[`claim_${pollId}`] || "", name: "" });
      setClaimed((c) => ({ ...c, [pollId]: r.data.points || 0 }));
    } catch (e2) {
      if (e2?.response?.status === 409) setClaimed((c) => ({ ...c, [pollId]: 0 }));
      else setErr(e2?.response?.data?.detail || "Claim failed");
    }
  };

  const enter = async (contestId) => {
    setBusy(true); setErr("");
    try {
      await axios.post(`${B}/api/engagement/public/${stationId}/contests/${contestId}/enter`, {
        name: f[`n_${contestId}`] || "", email: f[`e_${contestId}`] || "",
        answer: f[`a_${contestId}`] || "", consent: !!f[`c_${contestId}`] });
      setEntered((v) => ({ ...v, [contestId]: true }));
    } catch (e2) {
      if (e2?.response?.status === 409) setEntered((v) => ({ ...v, [contestId]: true }));
      else setErr(e2?.response?.data?.detail || "Entry failed");
    }
    setBusy(false);
  };

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const reset = (t) => { setTab(t); setDone(""); setErr(""); setF({}); setPts(0); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      let msg, awarded = 0;
      if (tab === "request") {
        const r = await axios.post(`${B}/api/request-line/public/${stationId}/submit`, {
          listener_name: f.name || "", song_title: f.song || "", artist: f.artist || "",
          shoutout_text: f.note || "", email: f.email || "", location: f.location || "", consent: true });
        msg = r.data.message; awarded = r.data.points_awarded || 0;
      } else if (tab === "dedication") {
        const r = await axios.post(`${B}/api/engagement/public/${stationId}/dedication`, {
          sender_name: f.name || "", recipient_name: f.recipient || "", dtype: f.dtype || "shoutout",
          message: f.note || "", song: f.song || "", email: f.email || "", consent: !!f.consent });
        msg = r.data.message; awarded = r.data.points_awarded || 0;
      } else {
        const r = await axios.post(`${B}/api/engagement/public/${stationId}/message`, {
          name: f.name || "", email: f.email || "", phone: f.phone || "",
          message: f.note || "", consent: !!f.consent });
        msg = r.data.message; awarded = r.data.points_awarded || 0;
      }
      setDone(msg || "Sent!"); setPts(awarded);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong — try again.");
    }
    setBusy(false);
  };

  if (!station) return <div className="min-h-screen bg-[#0c0e14] flex items-center justify-center text-white/50"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (station.missing) return <div className="min-h-screen bg-[#0c0e14] flex items-center justify-center text-white/60 text-sm">Station not found.</div>;

  const consentLabel = "I agree the station may store this submission to respond and run its shows. No data is ever sold.";
  const tabs = [...TABS];
  if (station.rewards_enabled) tabs.push(["redeem", "Redeem", Gift]);
  if (wall?.enabled && (wall.items || []).length) tabs.push(["voices", "Voices", Mic]);

  return (
    <div className="min-h-screen bg-[#0c0e14] text-white" data-testid="public-engage-page">
      <div className="max-w-xl mx-auto px-4 py-10 sm:py-16">
        <div className="text-center mb-8">
          {station.branding?.logo_url && (
            <img src={`${B}${station.branding.logo_url}`} alt="" data-testid="engage-brand-logo"
              className="h-16 w-16 mx-auto mb-4 rounded-xl object-contain" />
          )}
          <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em]"
            style={{ borderColor: `${accent}66`, background: `${accent}1a`, color: accent }}>
            <Radio className="h-3 w-3" /> On Air &amp; Listening
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl font-black tracking-tight" data-testid="public-engage-station-name">{station.name}</h1>
          {station.branding?.headline && <div className="mt-2 text-lg font-bold" style={{ color: accent }} data-testid="engage-brand-headline">{station.branding.headline}</div>}
          <p className="mt-2 text-sm text-white/50">{station.branding?.tagline || "Request a song, send a dedication, or drop us a message."}</p>
        </div>

        <div className="flex gap-1.5 mb-6 flex-wrap">
          {tabs.map(([k, label, Icon]) => (
            <button key={k} onClick={() => reset(k)} data-testid={`engage-tab-${k}`}
              style={tab === k ? { background: accent, borderColor: accent, color: "#000" } : {}}
              className={`flex-1 min-w-[100px] inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-2.5 text-xs font-bold border transition ${
                tab === k ? "" : "border-white/15 text-white/60 hover:text-white"}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        {tab === "voices" ? (
          <PublicVoiceWall stationId={stationId} items={wall?.items || []} accent={accent} />
        ) : tab === "redeem" ? (
          <PublicRedeem stationId={stationId} accent={accent} inputCls={inputCls} />
        ) : tab === "vote" ? (
          <div className="space-y-4" data-testid="engage-polls">
            {!polls.length && <div className="rounded-xl border border-white/10 bg-white/[0.03] p-8 text-center text-sm text-white/50" data-testid="engage-no-polls">No live polls right now — check back soon!</div>}
            {polls.map((p) => (
              <div key={p.poll_id} className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid={`engage-poll-${p.poll_id}`}>
                <div className="text-sm font-bold mb-3">{p.question}</div>
                {(p.options || []).map((o) => {
                  const pct = p.total_votes ? Math.round((o.votes / p.total_votes) * 100) : 0;
                  return voted[p.poll_id] ? (
                    <div key={o.key} className="relative rounded-md border border-white/10 overflow-hidden mb-1.5">
                      <div className="absolute inset-y-0 left-0 bg-amber-400/25" style={{ width: `${pct}%` }} />
                      <div className="relative flex justify-between px-3 py-2 text-xs"><span>{o.label}</span><span className="text-white/60">{pct}%</span></div>
                    </div>
                  ) : (
                    <button key={o.key} onClick={() => vote(p.poll_id, o.key)} data-testid={`engage-vote-${p.poll_id}-${o.key}`}
                      className="w-full text-left rounded-md border border-white/15 px-3 py-2 text-xs mb-1.5 hover:border-amber-400/60 hover:bg-amber-400/10 transition">
                      {o.label}
                    </button>
                  );
                })}
                {voted[p.poll_id] && <div className="text-[11px] text-emerald-300 mt-1" data-testid={`engage-voted-${p.poll_id}`}>Thanks for voting! {p.total_votes} vote{p.total_votes === 1 ? "" : "s"} so far.</div>}
                {voted[p.poll_id] && station.rewards_enabled && claimed[p.poll_id] === undefined && (
                  <div className="mt-3 flex gap-2" data-testid={`engage-claim-form-${p.poll_id}`}>
                    <input type="email" value={f[`claim_${p.poll_id}`] || ""} onChange={(e) => set(`claim_${p.poll_id}`, e.target.value)}
                      placeholder="Email to claim your points" data-testid={`engage-claim-email-${p.poll_id}`} className={inputCls} />
                    <button onClick={() => claim(p.poll_id)} data-testid={`engage-claim-btn-${p.poll_id}`}
                      className="rounded-md px-4 text-xs font-bold text-black shrink-0" style={{ background: accent }}>Claim pts</button>
                  </div>
                )}
                {claimed[p.poll_id] !== undefined && (
                  <div className="text-[11px] mt-2 font-bold" style={{ color: accent }} data-testid={`engage-claimed-${p.poll_id}`}>
                    {claimed[p.poll_id] > 0 ? `+${claimed[p.poll_id]} points claimed! 🎉` : "Points already claimed for this poll."}
                  </div>
                )}
              </div>
            ))}
            {err && <div className="text-xs text-rose-300">{err}</div>}
          </div>
        ) : tab === "win" ? (
          <div className="space-y-4" data-testid="engage-contests">
            {!contests.length && <div className="rounded-xl border border-white/10 bg-white/[0.03] p-8 text-center text-sm text-white/50" data-testid="engage-no-contests">No contests running right now — stay tuned!</div>}
            {contests.map((c) => (
              <div key={c.contest_id} className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid={`engage-contest-${c.contest_id}`}>
                <div className="text-sm font-bold">{c.title}</div>
                {c.prize && <div className="text-xs text-amber-300 mt-1">🏆 {c.prize}</div>}
                {c.description && <div className="text-xs text-white/50 mt-1">{c.description}</div>}
                {entered[c.contest_id] ? (
                  <div className="mt-3 text-sm text-emerald-300 font-semibold" data-testid={`engage-entered-${c.contest_id}`}>You're in — good luck! 🍀</div>
                ) : (
                  <div className="mt-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <input value={f[`n_${c.contest_id}`] || ""} onChange={(e) => set(`n_${c.contest_id}`, e.target.value)} placeholder="Your name" data-testid={`engage-entry-name-${c.contest_id}`} className={inputCls} />
                      <input type="email" value={f[`e_${c.contest_id}`] || ""} onChange={(e) => set(`e_${c.contest_id}`, e.target.value)} placeholder="Email" data-testid={`engage-entry-email-${c.contest_id}`} className={inputCls} />
                    </div>
                    {c.question && <input value={f[`a_${c.contest_id}`] || ""} onChange={(e) => set(`a_${c.contest_id}`, e.target.value)} placeholder={c.question} data-testid={`engage-entry-answer-${c.contest_id}`} className={inputCls} />}
                    <label className="flex items-start gap-2 text-[11px] text-white/50">
                      <input type="checkbox" checked={!!f[`c_${c.contest_id}`]} onChange={(e) => set(`c_${c.contest_id}`, e.target.checked)} data-testid={`engage-entry-consent-${c.contest_id}`} className="mt-0.5" />
                      {consentLabel}
                    </label>
                    <button onClick={() => enter(c.contest_id)} disabled={busy} data-testid={`engage-enter-btn-${c.contest_id}`}
                      className="w-full rounded-lg bg-amber-400 text-black font-bold text-sm py-2.5 disabled:opacity-60">Enter contest</button>
                  </div>
                )}
              </div>
            ))}
            {err && <div className="text-xs text-rose-300">{err}</div>}
          </div>
        ) : done ? (
          <div className="rounded-xl border border-emerald-400/40 bg-emerald-400/10 p-8 text-center" data-testid="engage-success">
            <CheckCircle2 className="h-8 w-8 text-emerald-300 mx-auto mb-3" />
            <div className="text-sm font-semibold">{done}</div>
            {pts > 0 && <div className="mt-2 text-xs font-bold" style={{ color: accent }} data-testid="engage-points-awarded">+{pts} listener points earned! 🎉</div>}
            <button onClick={() => reset(tab)} data-testid="engage-again-btn"
              className="mt-4 rounded-full border border-white/20 px-4 py-2 text-xs font-bold text-white/70 hover:text-white">Send another</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid={`engage-form-${tab}`}>
            <input required value={f.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Your name" data-testid="engage-name" className={inputCls} maxLength={60} />
            {tab === "request" && (<>
              <div className="grid grid-cols-2 gap-3">
                <input required value={f.song || ""} onChange={(e) => set("song", e.target.value)} placeholder="Song title" data-testid="engage-song" className={inputCls} maxLength={120} />
                <input required value={f.artist || ""} onChange={(e) => set("artist", e.target.value)} placeholder="Artist" data-testid="engage-artist" className={inputCls} maxLength={120} />
              </div>
              <input value={f.note || ""} onChange={(e) => set("note", e.target.value)} placeholder="Shout-out with your request (optional)" data-testid="engage-note" className={inputCls} maxLength={140} />
              <div className="grid grid-cols-2 gap-3">
                <input type="email" value={f.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="Email (optional)" data-testid="engage-email" className={inputCls} />
                <input value={f.location || ""} onChange={(e) => set("location", e.target.value)} placeholder="Where you're listening from" data-testid="engage-location" className={inputCls} maxLength={120} />
              </div>
            </>)}
            {tab === "dedication" && (<>
              <div className="grid grid-cols-2 gap-3">
                <input required value={f.recipient || ""} onChange={(e) => set("recipient", e.target.value)} placeholder="Who's it for?" data-testid="engage-recipient" className={inputCls} maxLength={120} />
                <select value={f.dtype || "shoutout"} onChange={(e) => set("dtype", e.target.value)} data-testid="engage-dtype"
                  className={`${inputCls} appearance-none`}>
                  {DTYPES.map(([k, label]) => <option key={k} value={k} className="bg-[#171b26]">{label}</option>)}
                </select>
              </div>
              <textarea required value={f.note || ""} onChange={(e) => set("note", e.target.value)} rows={3} placeholder="Your dedication message" data-testid="engage-ded-message" className={inputCls} maxLength={2000} />
              <div className="grid grid-cols-2 gap-3">
                <input value={f.song || ""} onChange={(e) => set("song", e.target.value)} placeholder="Song to play (optional)" data-testid="engage-ded-song" className={inputCls} maxLength={200} />
                <input type="email" value={f.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="Email (optional)" data-testid="engage-ded-email" className={inputCls} />
              </div>
            </>)}
            {tab === "message" && (<>
              <textarea required value={f.note || ""} onChange={(e) => set("note", e.target.value)} rows={4} placeholder="Your message to the station…" data-testid="engage-message" className={inputCls} maxLength={4000} />
              <div className="grid grid-cols-2 gap-3">
                <input type="email" required value={f.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="Email (so we can reply)" data-testid="engage-msg-email" className={inputCls} />
                <input value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="Phone (optional)" data-testid="engage-phone" className={inputCls} maxLength={40} />
              </div>
            </>)}
            {tab !== "request" && (
              <label className="flex items-start gap-2 text-[11px] text-white/50 leading-relaxed">
                <input type="checkbox" required checked={!!f.consent} onChange={(e) => set("consent", e.target.checked)} data-testid="engage-consent" className="mt-0.5" />
                {consentLabel}
              </label>
            )}
            {err && <div className="text-xs text-rose-300" data-testid="engage-error">{err}</div>}
            <button type="submit" disabled={busy} data-testid="engage-submit" style={{ background: accent }}
              className="w-full rounded-lg text-black font-bold text-sm py-3 disabled:opacity-60">
              {busy ? "Sending…" : tab === "request" ? "Send my request" : tab === "dedication" ? "Send dedication" : "Send message"}
            </button>
          </form>
        )}
        {station.paypal_link && (
          <a href={station.paypal_link} target="_blank" rel="noreferrer" data-testid="engage-paypal-btn"
            className="mt-6 flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-bold transition hover:opacity-90"
            style={{ borderColor: `${accent}55`, background: `${accent}14`, color: accent }}>
            <Heart className="h-4 w-4" /> Support {station.name} — tip us on PayPal
          </a>
        )}
        <div className="mt-8 text-center text-[10px] text-white/30">Powered by BroadcastFlow AI · Your info is only used by the station — never sold.</div>
      </div>
    </div>
  );
}
